import axios from "axios";
import type { AxiosInstance, AxiosRequestConfig } from "axios";
import LogManager from "./LogManager";
import BaseService from "./BaseService";
import Oauth2Service from "./Oauth2Service";
import Config from "./Config";
import NetworkService from "./NetworkService";
import AppService from "./AppService";

/** Metodos HTTP que admite sendRequest. */
export type RequestMethod = 'GET' | 'POST';

/**
 * Handles low level -authenticated- connections with backend server.
 *
 * Used by:
 * - RestService
 * - OauthService
 *
 * @todo Keep only logging here. Move user messages up.
 */
class Request extends BaseService {
    // Create a client whose requests will be signed
    // static client = axios.create();
    static client: AxiosInstance;

    /**
     * Init client and init OAuth but only if we've already got an access token.
     */
    static init(): void {

      // Create a new client.
      const config = {
        // `baseURL` will be prepended to `url` unless `url` is absolute.
        baseURL: Config.getRestUrl(),
        // `withCredentials` indicates whether or not cross-site Access-Control requests
        // should be made using credentials
        withCredentials: false, // default
      }

      this.logger().debug('Request init', config);

      this.client = axios.create(config);
    }

    /**
     * Report request activity to the App component, if it is already there.
     *
     * The handler cannot be captured once on init(): index.js runs
     * AppService.init() and SiteAuth.init() before rendering React, so the
     * requests they make happen while there is still no App instance.
     *
     * @param value Whether a request is in flight.
     */
    static setLoadingState(value: boolean): void {
      AppService.appcomponent?.statusRequestCallback(value);
    }

    /**
     * Gets Axios client, create if not there.
     */
    static getClient(): AxiosInstance {
      if (! this.client) {
        this.init();
      }
      return this.client;
    }

    /**
     * Create request with params
     *
     * @param url absolute or relative
     * @param method GET or POST
     * @param data object with the params used in the request
     * @param withAuth if request needs Authentication header or not
     * @param retry Internal param, whether to retry request if auth fails.
     * @param signal Para abortar la petición.
     *
     * @return returns result of request
     */
    static async sendRequest<T = unknown>(
      url: string,
      method: RequestMethod,
      data: unknown,
      withAuth = true,
      retry = true,
      signal?: AbortSignal,
    ): Promise<T> {
      this.debug("Request.sendRequest: " + url, data);

      if (NetworkService.isOffline()) {
          this.logger().debug('Request blocked due to network offline');
          return Promise.reject({ type: 'network', message: 'offline' });
      }

      this.setLoadingState(true);

      // @todo Need this? Or for post only?
      const axiosConfig: AxiosRequestConfig = {
          headers: {
              'Content-Type': 'text/plain',
          },
          signal,
      };

      if (withAuth) {
        // @todo Change to await / sync, check refresh token time, save a request.
        const authData = await Oauth2Service.getAuthorization(true, signal);
        if (authData) {
            axiosConfig.headers!.Authorization = authData;
        } else {
            this.setLoadingState(false);
            return Promise.reject('We need authorization data to send this request')
        }
      }


      let result;
      if (method === 'POST') {
        const body = JSON.stringify(data);
        result = this.getClient().post(url, body, axiosConfig);
      } else {
        result = this.getClient().get(url, axiosConfig);
      }

      return result
        .then((response) => {
            this.setLoadingState(false);
          return response.data as T;
        })
        .catch(async (error) => {
            this.setLoadingState(false);

           if (error.response && withAuth && retry && error.response.status < 500) {
             // If it is an authentication error (400 - 500), we may handle it.
             const canAuth = await Oauth2Service.processAuthErrorResponse(error.response, signal);
             if (canAuth) {
               // Send new request but with retry = false.
               return this.sendRequest<T>(url, method, data, withAuth, false, signal);
             }
           }

           // Fallback, default error handling.
           return Promise.reject(LogManager.processRequestError(error));
        })
    }

    /**
     * Get request, some error responses are handled internally.
     */
    static async get<T = unknown>(url: string, withAuth?: boolean, signal?: AbortSignal): Promise<T> {
        return this.sendRequest<T>(url, 'GET', null, withAuth, true, signal);
    }

    /**
     * Post request, some error responses are handled internally.
     */
    static async post<T = unknown>(url: string, data: unknown, withAuth?: boolean): Promise<T> {
        return this.sendRequest<T>(url, 'POST', data, withAuth);
    }
}

export default Request;
